import React, {useState} from "react";
// nodejs library that concatenates classes
import classnames from "classnames";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { useNotification } from "providers/NotificationProvider";
// reactstrap components
import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Container,
  Row,
  Col,
} from "reactstrap";
// core components
import AuthHeader from "components/Headers/AuthHeader.js";
import AuthNavbar from "components/Navbars/AuthNavbar";
import userServices from "services/userServices";
import { useTranslation } from "context/TranslationProvider";
import helpers from "utils/helpers";

function Login() {
  const [email, setEmail] = useState("");
  const { t, changeLanguage } = useTranslation();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { notify } = useNotification();

  const signIn = () => {
    setIsSubmitting(true);
    const loginData = { email, password };

    userServices
      .login(loginData)
      .then((res) => {
        setIsSubmitting(false);
        const payload = res?.data?.data || res?.data;
        if (payload) {
          const user = payload.user || payload;
          const role = user.role?.slug || user.role;

          if (!helpers.isAdminRole(role)) {
            notify("error", t("toast.noAdminAccess", "This account does not have access to the Admin Dashboard."));
            return;
          }

          if (user.preferredLanguage) {
            changeLanguage(user.translations?.preferredLanguage || user.preferredLanguage);
          }

          login({ user });

          notify("success", t(res?.data?.messageKey || "toast.welcomeUser", res?.data?.message || t("toast.welcomeUser", "Welcome, :name!")).replace(":name", user.firstName || "User"));

          navigate("/admin/dashboard", { replace: true });
        }
      })
      .catch((err) => {
        setIsSubmitting(false);
        const status = err?.response?.status;
        const errorData = err?.response?.data;
        const apiError = errorData?.error;
        const msgKey = errorData?.messageKey;
        const message = errorData?.message;
        const params = errorData?.params;

        if (msgKey || message || apiError) {
          let translatedMessage = t(msgKey || apiError, message || t("toast.unexpectedError", "Unexpected error occurred."));
          if (params && typeof params === "object") {
            Object.entries(params).forEach(([key, value]) => {
              const valStr = String(value ?? "");
              translatedMessage = translatedMessage
                .replace(new RegExp(`{\\s*${key}\\s*}`, "g"), valStr)
                .replace(new RegExp(`{{\\s*${key}\\s*}}`, "g"), valStr)
                .replace(new RegExp(`:${key}\\b`, "g"), valStr);
            });
          }
          notify("error", translatedMessage);
        } else if (status === 404 || (apiError && typeof apiError === "string" && apiError.toLowerCase().includes("not found"))) {
          notify("warning", t("toast.invalidEmail", "Invalid email format"));
        } else if (status === 401) {
          notify("error", t("toast.loginRequired", "To access the offer feature, please log in to your account."));
        } else {
          notify("error", t("toast.unexpectedError", "Unexpected error occurred."));
        }
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    signIn();
  };

  return (
    <>
      <AuthNavbar
        logo={{
          innerLink: "/",
          imgSrc: require("assets/img/images/logo.png"),
          imgAlt: "...",
        }}
      />
      <AuthHeader
        title={t("auth.login.welcomeBack", "Welcome Back")}
        lead={t("auth.login.lead", "Please sign in to continue to your Admin Dashboard.")}
      />
      <Container className="mt--8 pb-5 px-3 px-md-4">
        <Row className="justify-content-center">
          <Col lg="5" md="7" xs="12">
            <Card className="bg-secondary border-0 mb-0 shadow">
              <CardBody className="px-lg-5 py-lg-4 px-4 py-4">
                <div className="text-center text-muted mb-4">
                  <small className="text-lg">{t("auth.login.signInCreds", "Sign in with credentials")}</small>
                </div>
                <Form role="form" onSubmit={handleSubmit}>
                  <FormGroup
                    className={classnames("mb-3", {
                      focused: email,
                    })}
                  >
                    <InputGroup className="input-group-merge input-group-alternative">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="ni ni-email-83" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        placeholder={t("auth.login.email", "Email")}
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </InputGroup>
                  </FormGroup>
                  <FormGroup
                    className={classnames({
                      focused: password,
                    })}
                  >
                    <InputGroup className="input-group-merge input-group-alternative">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="ni ni-lock-circle-open" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        placeholder={t("auth.login.password", "Password")}
                        type={showPassword ? "text" : "password"}
                        value={password}                      
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <InputGroupAddon addonType="append">
                        <InputGroupText
                        className="password-toggle"
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          <i
                            className={
                              showPassword ? "fa fa-eye-slash" : "fa fa-eye"
                            }
                          />
                        </InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </FormGroup>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="customCheckLogin"
                    />
                    <label
                      className="form-check-label text-muted"
                      htmlFor="customCheckLogin"
                    >
                      {t("auth.login.rememberMe", "Remember Me")}
                    </label>
                  </div>
                  <div className="text-center">
                    <Button className="my-4" color="warning" type="submit" disabled={isSubmitting}>
                      {isSubmitting ? t("auth.login.button.signingIn", "Signing in...") : t("auth.login.button.signIn", "Sign in")}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
            <Row className="mt-3">
              <Col xs="12" className="text-center text-md-left">
                <Link to="/forgot-password" className="text-black">
                  <small className="font-weight-bold">{t("auth.login.forgotPassword", "Forgot Password?")}</small>
                </Link>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default Login;
